import React from 'react';

import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { CheckCircleIcon } from '@heroicons/react/24/outline';

import { DashboardSection } from './DashboardSection';

import { Heading, Text } from '../../design-system/components/Typography';

import { Button } from '../../design-system/components/Button';

import { spacing } from '../../design-system/tokens/spacing';

import { typography } from '../../design-system/tokens/typography';

import { DashboardSubscriptionView } from '../../types/dashboard';
import { CheckoutPlan } from '../../types/payment';

import { paymentService } from '../../services/payments';
import { redirectToXpayCheckout } from '../../utils/xpayCheckout';



const CardInner = styled.div`

  display: flex;

  flex-direction: column;

  gap: ${spacing[5]};

  height: 100%;

`;



const HeaderRow = styled.div`

  display: flex;

  align-items: flex-start;

  justify-content: space-between;

  gap: ${spacing[3]};

  flex-wrap: wrap;

`;



const StatusBadge = styled.span<{ $variant: 'success' | 'warning' | 'default' }>`

  display: inline-flex;

  align-items: center;

  gap: ${spacing[1]};

  padding: ${spacing[1]} ${spacing[3]};

  border-radius: 9999px;

  font-size: ${typography.fontSize.sm};

  font-weight: ${typography.fontWeight.medium};

  background: ${props => {

    if (props.$variant === 'success') return props.theme.colors.successBg ?? `${props.theme.colors.success}18`;

    if (props.$variant === 'warning') return props.theme.colors.warningBg ?? `${props.theme.colors.warning}18`;

    return props.theme.colors.surfaceHover;

  }};

  color: ${props => {

    if (props.$variant === 'success') return props.theme.colors.success;

    if (props.$variant === 'warning') return props.theme.colors.warning;

    return props.theme.colors.textSecondary;

  }};

`;



const Amount = styled.p`

  font-size: ${typography.fontSize.metric};

  font-weight: ${typography.fontWeight.semibold};

  line-height: ${typography.lineHeight.tight};

  margin: 0;

  color: ${props => props.theme.colors.text};

`;



const SupportingText = styled.p`

  margin: ${spacing[1]} 0 0 0;

  font-size: ${typography.fontSize.sm};

  line-height: ${typography.lineHeight.normal};

  color: ${props => props.theme.colors.textSecondary};

  font-family: ${typography.fontFamily.primary};

`;



const Actions = styled.div`

  display: flex;

  flex-wrap: wrap;

  gap: ${spacing[3]};

  margin-top: auto;



  @media (max-width: 480px) {

    flex-direction: column;



    button {

      width: 100%;

    }

  }

`;



interface SubscriptionStatusCardProps {

  subscription: DashboardSubscriptionView;

}



export const SubscriptionStatusCard: React.FC<SubscriptionStatusCardProps> = ({

  subscription,

}) => {

  const { t } = useTranslation();

  const navigate = useNavigate();



  const [paying, setPaying] = React.useState(false);
  const [plan, setPlan] = React.useState<CheckoutPlan | null>(null);

  React.useEffect(() => {
    const paymentId = subscription.payment?.id;
    if (!paymentId || !subscription.showPayNow) {
      setPlan(null);
      return;
    }
    let cancelled = false;
    paymentService.getCheckoutPlan(paymentId).then(next => {
      if (!cancelled) setPlan(next);
    }).catch(() => {
      if (!cancelled) setPlan(null);
    });
    return () => {
      cancelled = true;
    };
  }, [subscription.payment?.id, subscription.showPayNow]);

  const handleReceipt = async () => {

    if (!subscription.payment?.id) return;

    try {

      const blob = await paymentService.downloadReceipt(subscription.payment.id);

      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');

      a.href = url;

      a.download = `receipt-${subscription.year}.pdf`;

      a.click();

      URL.revokeObjectURL(url);

    } catch {

      navigate('/payments');

    }

  };

  const handlePayNow = async () => {
    if (!subscription.payment?.id) {
      navigate('/payments');
      return;
    }
    try {
      setPaying(true);
      await redirectToXpayCheckout(subscription.payment.id);
    } catch (err) {
      setPaying(false);
      console.error('Checkout failed:', err);
      alert(err instanceof Error ? err.message : t('payments.checkoutError'));
    }
  };



  const title = t('dashboard.subscription.title', { year: subscription.year });



  if (!subscription.payment) {

    return (

      <DashboardSection>

        <CardInner>

          <HeaderRow>

            <Heading variant="h3" as="h2" style={{ fontSize: typography.fontSize.cardHeading, margin: 0 }}>

              {title}

            </Heading>

          </HeaderRow>

          <SupportingText>{t('dashboard.subscription.noPayment')}</SupportingText>

          <Actions>

            <Button variant="outline" size="md" onClick={() => navigate('/payments')}>

              {t('dashboard.subscription.paymentHistory')}

            </Button>

          </Actions>

        </CardInner>

      </DashboardSection>

    );

  }



  return (

    <DashboardSection>

      <CardInner>

        <HeaderRow>

          <Heading variant="h3" as="h2" style={{ fontSize: typography.fontSize.cardHeading, margin: 0 }}>

            {title}

          </Heading>

          {subscription.statusLabel && (

            <StatusBadge $variant={subscription.isPaid ? 'success' : subscription.isPending ? 'warning' : 'default'}>

              {subscription.isPaid && <CheckCircleIcon width={16} height={16} aria-hidden />}

              {subscription.statusLabel}

            </StatusBadge>

          )}

        </HeaderRow>



        <div>

          {subscription.formattedAmount && <Amount>{subscription.formattedAmount}</Amount>}

          {subscription.isPending && subscription.dueDateLabel && (

            <SupportingText>{subscription.dueDateLabel}</SupportingText>

          )}

          {subscription.isPaid && subscription.paidOnLabel && (

            <SupportingText>{subscription.paidOnLabel}</SupportingText>

          )}

        </div>



        <Actions>

          {subscription.isPaid && subscription.hasReceipt && (

            <Button variant="primary" size="md" onClick={handleReceipt}>

              {t('dashboard.subscription.downloadReceipt')}

            </Button>

          )}

          {subscription.showPayNow && plan && plan.isBundled && (
            <SupportingText>
              {t('payments.arrearsNotice', {
                years: plan.years.join(' + '),
                amount: plan.totalAmount.toFixed(2),
              })}
            </SupportingText>
          )}

          {subscription.showPayNow && (

            <Button variant="primary" size="md" onClick={handlePayNow} disabled={paying}>

              {paying
                ? t('payments.checkoutStarting')
                : plan && plan.isBundled
                  ? t('payments.payBundled', { years: plan.years.join(' + ') })
                  : t('dashboard.subscription.payNow')}

            </Button>

          )}

          <Button variant="outline" size="md" onClick={() => navigate('/payments')}>

            {t('dashboard.subscription.paymentHistory')}

          </Button>

        </Actions>

      </CardInner>

    </DashboardSection>

  );

};



export default SubscriptionStatusCard;


